/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * What a guide pick would change against the reader's declared product, in the PICK's voice
 * (plan: docs/owner-notes-guide-funnel-plan.md §3 WP3; design brief v4, 2026-09-15).
 *
 * `compareRows()` already decides WHAT differs — the tier, the paired score rows, battery, the
 * differing specs; `curveVerdict`/`bandVerdicts` already decide what the curves say. This module
 * only turns that into the one line a roster row prints and the sentence a pick card opens with,
 * so a clause can never say something the "Versus yours" block below it contradicts.
 *
 * The rules it enforces (all inherited, none invented here):
 *  - both values on every numeric clause, never a bare delta, never price, never the SG score
 *    (Constraint `f80d2e70`);
 *  - the widest band gap leads, then Sound, ANC, battery, then ONE spec — at most four clauses
 *    (design brief Q0, "lean: widest gap first, at most four, 'about the same' stays a clause");
 *  - a row that is about the same on all of them says so in three words (plan §1, "Decisions
 *    settled": `About the same as yours`);
 *  - the "yours not measured" disclosure is said ONCE, on the first row, and lives on the chip
 *    otherwise (Directive `21f605aa`: never per card);
 *  - never a "Not measured" clause per axis (Rejected `21f605aa`).
 *
 * Pure: no React, no fetches, tested in `tests/owner-verdict.spec.ts`.
 */
import {
  axisScore,
  hoursText,
  sentenceCase,
  VERDICT_DB,
  type BandVerdict,
  type CompareRow,
  type Comparison,
  type DeltaNumbers,
  type Direction,
  type ScaleRow,
  type TextRow,
} from './deltas'

/** The two numbers behind a clause: this pick's, and the reader's. Kept apart rather than
 *  pre-joined into "4.6 vs 4.8" so every surface can colour them — blue for the pick, orange
 *  for the reader — which is what says WHOSE each number is without a word for it. */
export interface ClauseValues {
  theirs: string
  mine: string
}

/**
 * One thing this pick would change. `text` is the clause in the pick's voice ("Scores higher on
 * sound"); `values` is the pair behind it, rendered as one unbreakable unit with the text and
 * never on its own; `note` is the rare phrase that is NOT a pair (the "yours not measured"
 * disclosure, the list of identical specs). `dir` is the direction of the DIFFERENCE, not a
 * judgement: on frequency response louder is a difference, not a verdict (`curveVerdict`), so
 * no surface colours a band clause by it.
 */
export interface VerdictClause {
  text: string
  values: ClauseValues | null
  note?: string | null
  dir: 'up' | 'down' | 'same'
}

export interface Verdict {
  clauses: VerdictClause[]
}

export interface VerdictInput {
  /** `compareRows(declared, pick)`; null when there is nothing to compare. */
  comparison: Comparison | null
  /** The widest band of the pair's curves (`widestBand(bandVerdicts(mine, theirs))`). */
  band: BandVerdict | null
  /** The reader's declared product. */
  mine: DeltaNumbers
  /** The pick. */
  theirs: DeltaNumbers
  /** `labTested(theirs)` — a ClearBuy id or any axis, NOT the context's `lab_tested` flag
   *  (which means "has curves": a scored pick with no curves is still lab-tested). */
  theirsLabTested: boolean
  /** `labTested(mine)`. */
  mineLabTested: boolean
  /** 0-based row order. The "yours not measured" disclosure is added on row 0 only. */
  rowIndex?: number
}

/** At most four: past it a roster line wraps to three rows and nobody reads the fourth. */
export const MAX_CLAUSES = 4
/** The whole line when nothing differs (plan §1). */
export const SAME_AS_YOURS = 'About the same as yours'
/** Said once per pick, never per axis (Rejected `21f605aa`). */
export const NOT_LAB_TESTED = 'Not lab-tested'
/** Which side is missing, in the reader's words — the same phrase `compareRows` puts on an
 *  absent row, repeated here for the case where the reader's product has no axis at all and
 *  `compareRows` therefore emits no row to carry it. */
export const NOT_MEASURED = 'yours not measured'
/** The specs tier's closing clause; its values are the identical spec labels. */
export const SAME_LABEL = 'Same as yours'
/** Nothing to compare but the curves: DealHunt could not resolve this pick, so there are no
 *  scores, no battery figure and no spec sheet on its side — only the lab, which is keyed by
 *  the article's own ClearBuy id. */
export const CURVES_ONLY = 'Lab curves only'
export const CURVES_ONLY_NOTE = '(no scores or specs to compare)'

/** A clause plus whether it states a DIFFERENCE — "about the same" clauses do not, and a row
 *  made only of those collapses to `SAME_AS_YOURS`. */
interface Candidate {
  clause: VerdictClause
  differs: boolean
}

function capitalise(text: string): string {
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text
}

/** A clause label from a schema label: "Battery Life With Case" → "Battery life with case". */
const label = (raw: string): string => capitalise(sentenceCase(raw))

/** `Direction` has a fourth value — a difference with no better side (two IP ratings, two
 *  codec lists). It is still a difference, so it keeps `differs`, but it carries no verdict. */
function clauseDir(direction: Direction): 'up' | 'down' | 'same' {
  if (direction === 'up' || direction === 'down') {
    return direction
  }
  return 'same'
}

/** The band with the largest mean gap — what `curveVerdict` keeps for a card, as the verdict
 *  object itself so the clause can name the band and its range. */
export function widestBand(verdicts: readonly BandVerdict[]): BandVerdict | null {
  let best: BandVerdict | null = null
  for (const v of verdicts) {
    if (!best || Math.abs(v.db) > Math.abs(best.db)) {
      best = v
    }
  }
  return best
}

/**
 * The curve clause, from the frequency response — the one curve a pick block charts (design
 * brief Q00: three charts per roster row would be twenty-seven in Quick picks). Attenuation
 * speaks through the ANC score clause instead, so there is exactly one band clause and one
 * place it can come from. Under `VERDICT_DB` the curves are close — the same threshold the
 * chips, the callout and the sparkline tint use, so no surface calls a pair "the same" while
 * another prints a number for it.
 */
export function bandClause(band: BandVerdict): VerdictClause {
  if (Math.abs(band.db) < VERDICT_DB) {
    return { text: `About the same in the ${band.band.name}`, values: null, dir: 'same' }
  }
  // No value pair: two curves have no single number each, and the gap is already stated
  // relative to the reader's ("5 dB louder in the bass" can only mean louder than theirs). The
  // band name is the range in words, which is what a one-line clause has room for.
  return {
    text: `${Math.abs(Math.round(band.db))} dB ${band.db > 0 ? 'louder' : 'quieter'} in the ${
      band.band.name
    }`,
    values: null,
    dir: band.db > 0 ? 'up' : 'down',
  }
}

/** Sound and ANC in the pick's voice; any other axis falls back to its label. */
function scaleText(row: ScaleRow): string {
  if (row.key === 'sound') {
    if (row.direction === 'same') {
      return 'Sounds about the same'
    }
    return row.direction === 'up' ? 'Scores higher on sound' : 'Scores lower on sound'
  }
  if (row.key === 'anc') {
    if (row.direction === 'same') {
      return 'ANC about the same'
    }
    return row.direction === 'up' ? 'Blocks more' : 'Blocks less'
  }
  const word =
    row.direction === 'same' ? 'about the same' : `${row.direction === 'up' ? 'higher' : 'lower'}`
  return `${label(row.label)} ${word}`
}

function scaleCandidate(row: ScaleRow): Candidate {
  return {
    clause: {
      text: scaleText(row),
      values: { theirs: row.theirs.toFixed(1), mine: row.mine.toFixed(1) },
      dir: clauseDir(row.direction),
    },
    differs: row.direction !== 'same',
  }
}

function batteryCandidate(
  row: TextRow,
  mineHours: number | null,
  theirHours: number | null
): Candidate {
  const diff = mineHours !== null && theirHours !== null ? theirHours - mineHours : null
  const text =
    row.direction === 'same' || diff === null
      ? 'About the same per charge'
      : `${hoursText(Math.abs(diff))} ${diff > 0 ? 'longer' : 'shorter'} per charge`
  return {
    clause: {
      text,
      values: { theirs: row.theirs, mine: row.mine },
      dir: clauseDir(row.direction),
    },
    differs: row.direction !== 'same',
  }
}

/**
 * The one spec clause. A list row already states the difference in words ("adds LDAC") and
 * repeating the whole list after "vs" would double the clause — the same rule `VersusYours`
 * follows for its rows.
 */
function specCandidate(row: CompareRow): Candidate | null {
  if (row.kind === 'scale') {
    return {
      clause: {
        text: label(row.label),
        values: { theirs: row.theirs.toFixed(1), mine: row.mine.toFixed(1) },
        dir: clauseDir(row.direction),
      },
      differs: row.direction !== 'same',
    }
  }
  if (row.kind === 'text') {
    return {
      clause: {
        text: row.note ? capitalise(row.note) : label(row.label),
        values: row.note ? null : { theirs: row.theirs, mine: row.mine },
        dir: clauseDir(row.direction),
      },
      differs: true,
    }
  }
  // An absent row is a disclosure, not a spec; the block says "not lab-tested" once instead.
  return null
}

/**
 * The clauses one pick prints against the declared product, widest band gap first.
 *
 * Tiers (the ones `compareRows` computes, one line tall):
 *  - `lab` — both sides have curves: the band clause leads, then the paired scores.
 *  - `scored` — no curves, so no band clause; the paired scores lead.
 *  - `specs` — battery, one differing spec, and what is identical.
 *  - nothing but curves (DealHunt could not resolve the pick): one `Lab curves only` clause in
 *    front of the band, so the line is never read as a full comparison.
 *  - the pick has no lab and the reader's product does: one `Not lab-tested` clause, then what
 *    both sides do carry. With neither measured the clause is dropped — nothing was lost.
 *  - the READER's product has no lab: the pick's own scores are stated instead of paired, with
 *    "yours not measured" on the first row only.
 */
export function buildVerdict(input: VerdictInput): Verdict {
  const { comparison, band, mine, theirs, theirsLabTested, mineLabTested, rowIndex } = input
  const rows: CompareRow[] = comparison?.rows ?? []
  const details: CompareRow[] = comparison?.details ?? []
  const out: Candidate[] = []

  // Only when the READER's side has a lab result to compare against: with neither measured
  // there was never a comparison to miss, and the chip already carries that disclosure
  // (Directive `21f605aa`) — design brief §5, fallback D says nothing about the lab.
  if (!theirsLabTested && mineLabTested) {
    out.push({ clause: { text: NOT_LAB_TESTED, values: null, dir: 'same' }, differs: true })
  } else if (theirsLabTested && comparison?.tier === 'lab' && band) {
    const clause = bandClause(band)
    out.push({ clause, differs: clause.dir !== 'same' })
  }

  // The disclosure is one phrase per page surface, on the first row (Directive 21f605aa).
  let disclosed = false
  const disclose = (): string | null => {
    if (rowIndex !== 0 || disclosed) {
      return null
    }
    disclosed = true
    return NOT_MEASURED
  }

  for (const key of ['sound', 'anc']) {
    const row = rows.find(r => r.key === key)
    if (row?.kind === 'scale') {
      out.push(scaleCandidate(row))
    } else if (row?.kind === 'absent' && row.note) {
      // The pick was measured on this axis and the reader's product was not: state the pick's
      // figure rather than dropping the axis.
      out.push({
        clause: {
          text: `${label(row.label)} ${row.text}`,
          values: null,
          note: disclose(),
          dir: 'same',
        },
        differs: true,
      })
    } else if (!row && !mineLabTested) {
      // Nothing to pair at all (the reader's product carries no axis, so `compareRows` emits no
      // row for it): the pick's own score, said plainly.
      const score = axisScore(theirs.axes, key)
      if (score) {
        out.push({
          clause: {
            text: `${score.label} ${score.value.toFixed(1)} /${score.max}`,
            values: null,
            note: disclose(),
            dir: 'same',
          },
          differs: true,
        })
      }
    }
  }

  const battery = rows.find(r => r.key === 'battery')
  if (battery?.kind === 'text') {
    out.push(batteryCandidate(battery, mine.batteryHours, theirs.batteryHours))
  }

  // The first DIFFERING SPEC, not `details[0]`: with both sides measured `details[0]` is
  // Isolation — a third score after Sound and ANC, where the clause budget is better spent on
  // what the spec sheets differ on ("Adds LDAC"). Isolation keeps its row in "Versus yours"
  // behind "All details".
  const spec = details.find(row => row.kind === 'text') ?? details[0] ?? null
  const specClause = spec ? specCandidate(spec) : null
  if (specClause) {
    out.push(specClause)
  }

  if (comparison?.tier === 'specs' && comparison.same.length > 0) {
    out.push({
      clause: { text: SAME_LABEL, values: null, note: comparison.same.join(', '), dir: 'same' },
      differs: true,
    })
  }

  // Curves and nothing else (an unresolved pick): say so, rather than letting a band clause —
  // or worse, the "about the same" collapse below — read as a comparison of two spec sheets.
  const comparable = rows.length > 0 || details.length > 0 || (comparison?.same.length ?? 0) > 0
  if (comparison && !comparable) {
    out.unshift({
      clause: { text: CURVES_ONLY, values: null, note: CURVES_ONLY_NOTE, dir: 'same' },
      differs: true,
    })
  }

  const differing = out.filter(candidate => candidate.differs)
  if (differing.length === 0) {
    return { clauses: [{ text: SAME_AS_YOURS, values: null, dir: 'same' }] }
  }
  // Differences take the four slots first, each group keeping its order. A lab pair that is
  // close on the curves, Sound, ANC and battery but adds LDAC used to spend all four slots
  // saying "about the same" and drop the one thing it changes, which sat at index 4. The band
  // still leads whenever the band is itself a difference — it is first among the differing.
  const clauses = [...differing, ...out.filter(candidate => !candidate.differs)]
    .slice(0, MAX_CLAUSES)
    .map(candidate => candidate.clause)
  return { clauses }
}
